import { type PlayerCard, playerCardSchema } from "@app/protocol";
import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../config";
import { useStore } from "../store";

/** A card opened again within a minute asks nothing (review 0017 S8, U18). */
const FRESH_MS = 60_000;
/** The card gives up waiting after this and offers a retry (U10). */
const TIMEOUT_MS = 5_000;

const cache = new Map<string, { at: number; card: PlayerCard }>();

export type CardState =
  | { status: "loading" }
  | { status: "ready"; card: PlayerCard }
  | { status: "failed" };

async function fetchCard(session: string, userId: string): Promise<PlayerCard | null> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(`${API_BASE}/players/${encodeURIComponent(userId)}/card`, {
      headers: { Authorization: `Bearer ${session}` },
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const parsed = playerCardSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

/** One player's card, fetched when it opens, kept a minute; `retry` asks again after a failure. */
export function usePlayerCard(userId: string): { state: CardState; retry: () => void } {
  const session = useStore((s) => s.session);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<CardState>(() => {
    const cached = cache.get(userId);
    return cached ? { status: "ready", card: cached.card } : { status: "loading" };
  });
  useEffect(() => {
    // A retry never trusts what an earlier request left behind.
    if (attempt > 0) cache.delete(userId);
    const cached = cache.get(userId);
    if (cached && Date.now() - cached.at < FRESH_MS) {
      setState({ status: "ready", card: cached.card });
      return;
    }
    if (session === null) return;
    let live = true;
    setState({ status: "loading" });
    void fetchCard(session, userId).then((card) => {
      if (card !== null) cache.set(userId, { at: Date.now(), card });
      if (live) setState(card === null ? { status: "failed" } : { status: "ready", card });
    });
    return () => {
      live = false;
    };
  }, [session, userId, attempt]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, retry };
}
